import { useCallback, useEffect, useRef } from 'react';

import { useStateWithRef } from '@/shared/hooks/use-state-with-ref';

/**
 * Buffers a free-text input locally so typing doesn't ripple through the
 * working copy; flushes on blur and on unmount (a selection switch unmounts
 * without a blur). An upstream value that differs from the last synced one
 * (e.g. a property change clearing the leaf) re-seeds the buffer.
 */
export function useBufferedValue(
  value: string,
  flush: (next: string) => void,
): { value: string; onChange: (next: string) => void; onBlur: () => void } {
  const [buffer, setBuffer, bufferRef] = useStateWithRef(value);

  // The last value in sync with upstream; a differing incoming value is an
  // external change and wins over the buffer (re-seeded during render).
  const syncedRef = useRef(value);
  if (value !== syncedRef.current) {
    syncedRef.current = value;
    setBuffer(value);
  }

  const flushRef = useRef(flush);
  flushRef.current = flush;

  // Reads only refs, so a stable identity is safe for the unmount effect.
  const doFlush = useCallback(() => {
    if (bufferRef.current !== syncedRef.current) {
      syncedRef.current = bufferRef.current;
      flushRef.current(bufferRef.current);
    }
  }, [bufferRef]);

  useEffect(() => () => doFlush(), [doFlush]);

  return {
    value: buffer,
    onChange: setBuffer,
    onBlur: doFlush,
  };
}
